<template>
  <div>
    <div class="mb-8">
      <h2 class="title mb-4">1. Modelo de impressão</h2>
      <v-select
        v-model="selectedTemplate"
        :items="printingTemplates"
        placeholder="Modelo de impressão"
        persistent-placeholder
        outlined
        dense
        item-text="name"
        item-value="id"
        :height="48"
        attach="#template_selec"
      >
        <template #append-outer>
          <div id="template_selec"></div>
        </template>
      </v-select>
    </div>

    <div>
      <div class="mb-4 d-flex align-center justify-space-between">
        <h2 class="title">2. Relatório</h2>
        <v-btn
          text
          color="primary"
          class="text-none px-3 rounded-lg"
          @click="fillDefaultReportAndReferences"
          :height="40"
          >Desfazer alterações</v-btn
        >
      </div>

      <v-textarea
        label="Texto do relatório"
        v-model="report"
        counter="5000"
        :rows="45"
        outlined
        class="mb-4"
        :key="`report_${reportKey}`"
        @keyup="setToManual"
        :hint="
          manualReport
            ? `O relatório está no modo manual. Para habilitar atualizações automáticas clique em 'Desfazer alterações'.`
            : null
        "
      ></v-textarea>
      <v-textarea
        label="Referências"
        v-model="references"
        counter="5000"
        :rows="5"
        outlined
        class="mb-8"
        :key="`references_${reportKey}`"
      ></v-textarea>
    </div>
    <div class="d-flex align-center justify-end">
      <download-frame
        :url="printReportUrl"
        remoteUrl
        alwaysForceDownload
        :onBeforeDownload="saveAllAnswers"
        :disabled="isEmpty(report)"
        v-slot="{ loading }"
      >
        <v-btn outlined color="primary" class="text-none px-6 rounded-lg" :height="40" :loading="loading"
          >Imprimir relatório</v-btn
        >
      </download-frame>
    </div>
  </div>
</template>
<script>
import { Computed, ObjectLiteral } from '@/_typedefs'
import { createNamespacedHelpers } from 'vuex'
import DownloadFrame from '@/components/DownloadFrame.vue'
import { isEmpty } from 'lodash'
import { AGE_BREAKPOINTS } from '@/components/AttendanceForm/general/constants/age'

const {
  mapMutations: newAttendanceFormMutations,
  mapGetters: newAttendanceFormGetters,
  mapActions: newAttendanceFormActions,
} = createNamespacedHelpers('newAttendanceForm')

export default {
  components: {
    DownloadFrame,
  },
  data() {
    return {
      report: null,
      references: null,
      active: true,
      selectedTemplate: null,
      printingTemplates: [],
      reportKey: 0,
      unwatch: null,
      manualReport: false,
    }
  },
  computed: {
    .../** @type {{attendance: Computed<ObjectLiteral>, treatments: Computed<ObjectLiteral[]>}} */ (
      newAttendanceFormGetters(['attendance', 'treatments', 'extraAnswers', 'parameters', 'questions', 'answers'])
    ),
    /** @type {Computed<string>} */
    printReportUrl() {
      return (
        `${this.$util.apiURL()}/attendance/report/print/${this.attendance.id}` +
        (this.selectedTemplate ? `/${this.selectedTemplate}` : '')
      )
    },
    isPatientAdult() {
      return this.$util.toAge(this.attendance.patient.birthday) >= AGE_BREAKPOINTS.child
    },

    defaultReferences() {
      const treatments = this.extraAnswers?.selectedTreatments
      if (!treatments?.length > 0) {
        return ''
      }
      const referencesArr = treatments.map((t) => t.references.map((r) => r.reference).join('\n'))
      return referencesArr.filter((r) => !!r).join('\n')
    },
    scores() {
      const scorad = {
        key: 'SCORAD',
        value: this.parameters['atopic-dermatitis:scorad']?.score,
      }
      const bsa = {
        key: 'BSA',
        value: this.parameters['atopic-dermatitis:easi-bsa']?.score?.bsa,
      }
      const easi = {
        key: 'EASI',
        value: this.parameters['atopic-dermatitis:easi-bsa']?.score?.easi,
      }
      const iga = {
        key: 'IGA',
        value: this.parameters['atopic-dermatitis:iga']?.score,
      }
      const dlqi = {
        key: 'DLQI',
        value: this.parameters['dlqi-parameter']?.score,
      }
      const adct = {
        key: 'ADCT',
        value: this.extraAnswers.adct?.adct?.value,
      }
      const poem = {
        key: 'POEM',
        value: this.extraAnswers.poem?.poem?.value,
      }
      const pt_iga = {
        key: 'PT-IGA',
        value: this.extraAnswers['pt-iga']?.ptiga?.value,
      }
      const wi_nrs = {
        key: 'WI-NRS',
        value: this.extraAnswers['wi-nrs']?.winrs?.value,
      }
      return [scorad, easi, bsa, iga, dlqi, adct, poem, pt_iga, wi_nrs].filter((score) => score.value)
    },
    diagnosticHypothesis() {
      const bsa =
        this.parameters['atopic-dermatitis:easi-bsa']?.score?.bsa &&
        this.parameters['atopic-dermatitis:easi-bsa'].score.bsa >= 10
      const easi =
        this.parameters['atopic-dermatitis:easi-bsa']?.score?.easi &&
        this.parameters['atopic-dermatitis:easi-bsa'].score.easi > 7
      const iga = this.parameters['atopic-dermatitis:iga']?.score && this.parameters['atopic-dermatitis:iga'].score >= 3
      const dlqi =
        this.parameters['dlqi-parameter']?.score &&
        (this.isPatientAdult
          ? this.parameters['dlqi-parameter'].score > 10
          : this.parameters['dlqi-parameter'].score > 12)
      const scorad =
        this.parameters['atopic-dermatitis:scorad']?.score && this.parameters['atopic-dermatitis:scorad'].score >= 25

      const check = scorad || easi || bsa || iga || dlqi
      return check ? 'dermatite atópica moderada a grave' : 'dermatite atópica'
    },
    complications() {
      const complicationsAnswers = this.answers['3']
      const complicationsQuestionData = this.questions['3'].questionData.items
      const complications = complicationsQuestionData
        .filter((item) => complicationsAnswers.selected?.includes(item.key))
        .map((item) => `${item.name}`)

      if (this.answers['4']?.locations.length > 0) {
        let currentHospitalization = 'Internação hospitalar'

        if (this.answers['4'].start_date_formatted && this.answers['4'].end_date_formatted) {
          currentHospitalization = `${currentHospitalization} de ${this.answers['4'].start_date_formatted} a ${this.answers['4'].end_date_formatted}`
        }

        currentHospitalization = `${currentHospitalization} em ${this.formattingTextInList(this.answers['4'].locations).join('')}`
        complications.push(currentHospitalization)
      }

      if (this.answers['4'].previousHospitalization.length > 0) {
        const previousHospitalizations = [...this.answers['4'].previousHospitalization]
          .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime())
          .map(
            (item) =>
              `Internação hospitalar de ${item.start_date_formatted} a ${
                item.end_date_formatted
              } em ${this.formattingTextInList(item.locations).join('')}`,
          )
        complications.push(...previousHospitalizations)
      }

      return complications
    },
    previousTreatments() {
      const localTreatmentskey = '2.1'
      const systemicTreatmentsKey = '2.2'
      const treatmentsTherapiesKey = '2.3'

      const systemicTreatments = this.previousTreatmentsMap(
        this.answers[systemicTreatmentsKey],
        this.questions[systemicTreatmentsKey],
      )
      const localTreatments = this.previousTreatmentsMap(
        this.answers[localTreatmentskey],
        this.questions[localTreatmentskey],
      )

      const treatmentsTherapies = this.previousTreatmentsMap(
        this.answers[treatmentsTherapiesKey],
        this.questions[treatmentsTherapiesKey],
      )

      const otherTreatments = this.answers['2.4']

      return [...localTreatments, ...systemicTreatments, ...treatmentsTherapies, otherTreatments].filter(
        (item) => !!item,
      )
    },

    currentTreatmentsMedications() {
      const extraAnswersSelectedTreatments = this.extraAnswers.selectedTreatments || []
      const selectedTreatments = extraAnswersSelectedTreatments.map((treatment) => treatment.medicine.name)

      const text =
        selectedTreatments.length > 1
          ? `${selectedTreatments.slice(0, -1).join(', ')} e ${selectedTreatments.slice(-1)[0]}`
          : selectedTreatments[0]

      return {
        medicines: selectedTreatments,
        text,
      }
    },
  },

  watch: {
    report(value) {
      if (isEmpty(value)) {
        return
      }
      this.updateExtraAnswer({ name: 'report', value })
    },
    references(value) {
      if (isEmpty(value)) {
        return
      }
      this.updateExtraAnswer({ name: 'references', value })
    },
  },

  methods: {
    ...newAttendanceFormMutations(['updateExtraAnswer']),
    ...newAttendanceFormActions(['saveAllAnswers', 'getParameter']),

    previousTreatmentsMap(answers, questions) {
      const selectedAnswers = (answers?.selected || [])
        .map((answer) => {
          return {
            key: answer,
            detail: answers.detail[answer],
            label: questions.questionData.options.find((option) => option.key === answer)?.label,
          }
        })
        .filter((answer) => !!answer.label)

      return selectedAnswers.map((answer) => `${answer.label}${answer.detail ? ' (' + answer.detail + ')' : ''}`)
    },

    formattingTextInList(arr) {
      if (arr.length === 1) {
        return arr
      }
      return arr.map((text, index, arr) =>
        index === arr.length - 1 ? ' e ' + text : text + (index === arr.length - 2 ? '' : ', '),
      )
    },
    getReportDefaultText({
      diagnosticHypothesis,
      scores,
      previousTreatments,
      complications,
      currentTreatmentsMedications,
    }) {
      const initialArticle = this.attendance.patient.assignedSex === 'F' ? 'A' : 'O'
      const age = this.$util.toAge(this.attendance.patient.birthday)
      const patientName = this.attendance.patient.socialName || this.attendance.patient.name

      const paragraphs = [
        `${initialArticle} paciente ${patientName}, ${age}, apresenta lesões eritemato-descamativas compatíveis com ${diagnosticHypothesis}. (CID: L20).`,
      ]

      if (scores.length > 0) {
        paragraphs.push(`\nApresenta:`)
        scores.forEach((score) => {
          if (!isNaN(score.value)) {
            paragraphs.push(` - ${score.key} = ${score.value}`)
          }
        })
      }

      paragraphs.push(`\nFez uso prévio de:`)

      previousTreatments.forEach((treatment, index, arr) => {
        paragraphs.push(` - ${treatment}${index === arr.length - 1 ? '.' : ';'}`)
      })

      paragraphs.push(
        `\nApesar do uso dos tratamentos por tempo e dose adequados, além de medidas comportamentais adequadas, evitando fatores desencadeantes, ${initialArticle.toLowerCase()} paciente não obteve melhora efetiva das suas lesões de pele e da sua qualidade de vida (falha terapêutica).`,
      )

      paragraphs.push(
        `\n${initialArticle} paciente apresenta lesões em áreas visíveis e apresenta prurido intenso, o que atrapalha nas atividades diárias, além de ser uma doença estigmatizante que prejudica o seu convívio social.`,
      )

      if (complications.length > 0) {
        paragraphs.push(`\nAlém disso, apresentou as seguintes complicações:`)

        complications.forEach((item, index, arr) => {
          paragraphs.push(` - ${item}${index === arr.length - 1 ? '.' : ';'}`)
        })
      }
      paragraphs.push(
        `\nSendo assim, solicito tratamento com ${currentTreatmentsMedications.text || '.................'}, para uso conforme receita em anexo.`,
      )

      paragraphs.push(
        `${
          currentTreatmentsMedications.medicines.length > 1
            ? currentTreatmentsMedications.text + ' são medicamentos que têm'
            : (currentTreatmentsMedications.text || '.................') + ' é um medicamento que tem'
        } alta eficácia na Dermatite Atópica. Com esse tratamento, a paciente poderá ter remissão parcial ou eventualmente total das lesões de pele e seus sintomas.`,
      )

      paragraphs.push(
        `\nComprometo-me a realizar seguimento do caso em consultas regulares para orientar o paciente quanto ao seguimento correto do tratamento, monitorar sua adesão às medidas propostas e observar os efeitos positivos do medicamento assim como tratar com rapidez e responsabilidade os possíveis efeitos adversos associados à terapêutica proposta.`,
      )

      paragraphs.push(`\nFico à disposição para eventuais esclarecimentos.`)

      paragraphs.push(`\nAtenciosamente,\n`)
      return paragraphs.join('\n')
    },

    async listTemplates() {
      try {
        const { items: templates } = await this.$api.getTemplates()
        this.printingTemplates = templates.map(({ id, name }) => ({ id, name }))
      } catch (error) {
        console.log(error)
      }
    },

    fillDefaultReportAndReferences() {
      this.updateExtraAnswer({ name: 'manualReport', value: false })
      this.manualReport = false
      this.report = this.getReportDefaultText({
        diagnosticHypothesis: this.diagnosticHypothesis,
        scores: this.scores,
        previousTreatments: this.previousTreatments,
        complications: this.complications,
        currentTreatmentsMedications: this.currentTreatmentsMedications,
      })
      this.references = this.defaultReferences
      this.reportKey += 1
    },
    isEmpty,

    setToManual(e) {
      this.updateExtraAnswer({ name: 'manualReport', value: true })
      this.manualReport = true
    },
  },

  async created() {
    await this.$nextTick()
    this.references = this.$store.getters['newAttendanceForm/extraAnswers']?.references
    this.report = this.$store.getters['newAttendanceForm/extraAnswers']?.report

    await this.listTemplates()

    this.manualReport = this.$store.getters['newAttendanceForm/extraAnswers']?.manualReport
    if (
      !this.$store.getters['newAttendanceForm/extraAnswers']?.manualReport ||
      ((!this.report || this.report.length === 0) && (!this.references || this.references.length === 0))
    ) {
      this.fillDefaultReportAndReferences()
    }

    this.unwatch = this.$store.subscribe((mutation, state) => {
      const listeners = {
        'newAttendanceForm/updateAnswer': () => {
          if (!this.$store.getters['newAttendanceForm/extraAnswers']?.manualReport) {
            this.fillDefaultReportAndReferences()
          }
        },
        'newAttendanceForm/updateParameter': () => {
          if (!this.$store.getters['newAttendanceForm/extraAnswers']?.manualReport) {
            this.fillDefaultReportAndReferences()
          }
        },
        'newAttendanceForm/updateExtraAnswer': () => {
          if (
            mutation.payload.name === 'report' ||
            mutation.payload.name === 'references' ||
            mutation.payload.name === 'manualReport'
          ) {
            return
          }
          if (!this.$store.getters['newAttendanceForm/extraAnswers']?.manualReport) {
            this.fillDefaultReportAndReferences()
          }
        },
      }

      listeners[mutation.type]?.()
    })
  },

  beforeDestroy() {
    this.unwatch?.()
  },
}
</script>
<style scoped>
.title {
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.15px;
  color: var(--v-onSurface-base);
}
.btn-txt {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
}
</style>
